import type { PointerState } from "../input/pointer";
import type { UnicodeArt } from "./unicodeArt";
import type { Eye } from "./eye";
import { eyeScale } from "./eye";

export type ScenePhase = "text" | "to-eye" | "eye" | "to-text";

export type Scene = {
  update: (
    dt: number,
    now: number,
    pointer: PointerState,
    w: number,
    h: number,
  ) => void;
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
};

/** Idle pastiradit 4s, idle eyes 6s, then roll to the other. */
const HOLD_TEXT = 4.0;
const HOLD_EYE = 6.0;
const TRANSITION = 2.0;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

export function createScene(art: UnicodeArt, eye: Eye): Scene {
  let phase: ScenePhase = "text";
  let timer = 0;
  let trans = 0;
  let reformQueued = false;

  art.opacity = 1;
  eye.opacity = 0;
  art.reform();
  art.scramble(0);

  const enter = (next: ScenePhase) => {
    phase = next;
    trans = 0;
    timer = 0;
    if (next === "to-text") {
      reformQueued = true;
    }
  };

  const update: Scene["update"] = (dt, now, pointer, w, h) => {
    const cx = w * 0.5;
    const cy = h * 0.5;

    if (phase === "text") {
      art.opacity = 1;
      eye.opacity = 0;
      art.update(dt, now);
      timer += dt;
      if (timer >= HOLD_TEXT) enter("to-eye");
    } else if (phase === "to-eye") {
      // Opacity crossfade only - scramble during fade flashes the wordmark.
      art.scramble(0);
      trans = Math.min(1, trans + dt / TRANSITION);
      const e = easeInOut(trans);
      art.opacity = 1 - e;
      eye.opacity = e;
      art.update(dt, now);
      eye.update(dt, pointer.x, pointer.y, cx, cy);
      if (trans >= 1) {
        phase = "eye";
        timer = 0;
        art.opacity = 0;
        eye.opacity = 1;
      }
    } else if (phase === "eye") {
      art.opacity = 0;
      eye.opacity = 1;
      eye.update(dt, pointer.x, pointer.y, cx, cy);
      timer += dt;
      if (timer >= HOLD_EYE) enter("to-text");
    } else if (phase === "to-text") {
      // Reform only while the wordmark is fully hidden, before fade-in.
      if (reformQueued && art.opacity <= 0.001) {
        art.reform();
        reformQueued = false;
      }
      art.scramble(0);
      trans = Math.min(1, trans + dt / TRANSITION);
      const e = easeInOut(trans);
      eye.opacity = 1 - e;
      art.opacity = e;
      art.update(dt, now);
      eye.update(dt, pointer.x, pointer.y, cx, cy);
      if (trans >= 1) {
        phase = "text";
        timer = 0;
        art.opacity = 1;
        eye.opacity = 0;
        art.scramble(0);
      }
    }
  };

  const draw: Scene["draw"] = (ctx, w, h) => {
    const cx = w * 0.5;
    const cy = h * 0.5;
    if (art.opacity > 0.001) {
      art.draw(ctx, cx, cy, w, h);
    }
    if (eye.opacity > 0.001) {
      eye.draw(ctx, cx, cy, eyeScale(w, h));
    }
  };

  return { update, draw };
}
